import { Link, useNavigate } from "react-router-dom";
import {
  Wrench,
  ArrowLeft,
  ArrowRight,
  Train,
  Zap,
  Building2,
  Clock,
} from "lucide-react";
import PnjLogo from "@/components/PnjLogo";

/**
 * FailureGuidance — sub-menu hub untuk modul panduan kegagalan.
 *   1. Rollingstock Guidance List  (aktif → /failure-guidance/rollingstock)
 *   2. Power Guidance List         (aktif → /failure-guidance/power)
 *   3. Facility Guidance List      (coming soon — not clickable)
 */
const SUBMENUS = [
  {
    key: "rollingstock",
    testid: "submenu-rollingstock",
    title: "Rollingstock Guidance List",
    desc: "Panduan penanganan kegagalan pada trainset — pintu, propulsi, brake, HVAC, dan sistem on-board.",
    icon: Train,
    accent: "from-[#0ea5e9] to-[#22c55e]",
    badgeCls: "bg-[#289643]/20 text-[#22c55e] border-[#22c55e]/30",
    badgeText: "Aktif",
    tags: ["Traction", "Doors", "Brake"],
    to: "/failure-guidance/rollingstock",
    disabled: false,
  },
  {
    key: "power",
    testid: "submenu-power",
    title: "Power Guidance List",
    desc: "Panduan gangguan sistem catu daya — traction power, auxiliary, dan gardu induk depo.",
    icon: Zap,
    accent: "from-[#f97316] to-[#facc15]",
    badgeCls: "bg-[#289643]/20 text-[#22c55e] border-[#22c55e]/30",
    badgeText: "Aktif",
    tags: ["Traction PSU", "Aux PSU", "Substation"],
    to: "/failure-guidance/power",
    disabled: false,
  },
  {
    key: "facility",
    testid: "submenu-facility",
    title: "Facility Guidance List",
    desc: "Panduan gangguan fasilitas depo — jalur, wesel, sistem sinyal, dan bangunan pendukung.",
    icon: Building2,
    accent: "from-[#f59e0b] to-[#dc2626]",
    badgeCls: "bg-[#f59e0b]/15 text-[#fbbf24] border-[#f59e0b]/30",
    badgeText: "Coming Soon",
    tags: ["Wesel", "Signal", "Track"],
    to: null,
    disabled: true,
  },
];

export default function FailureGuidance() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0c1b2a] text-white relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-25 fg-grid" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(245,158,11,0.14),transparent_55%)]" />
      </div>

      <header className="relative z-10 max-w-6xl mx-auto flex items-center justify-between px-6 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
          data-testid="back-to-menu"
        >
          <ArrowLeft className="h-4 w-4" />
          Menu Utama
        </Link>
        <PnjLogo size={32} />
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-10 pb-16">
        <div className="flex items-center gap-4 mb-10">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#f59e0b] to-[#dc2626] flex items-center justify-center shadow-lg shadow-orange-500/30">
            <Wrench className="h-8 w-8 text-[#0c1b2a]" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#fbbf24]">
              Sub Menu
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold">
              Failure Guidance List
            </h1>
          </div>
        </div>

        <p className="text-slate-300 max-w-2xl leading-relaxed mb-10">
          Pilih kategori panduan penanganan kegagalan operasional. Setiap modul
          berisi checklist penanganan cepat, prosedur eskalasi, dan referensi
          kontak lapangan.
        </p>

        <div className="grid gap-6 md:grid-cols-3">
          {SUBMENUS.map((m) => {
            const Icon = m.icon;
            const commonInner = (
              <>
                <div className="flex items-start justify-between">
                  <div
                    className={`h-14 w-14 rounded-xl bg-gradient-to-br ${m.accent} flex items-center justify-center shadow-lg ${
                      m.disabled ? "opacity-70" : "shadow-cyan-500/30"
                    }`}
                  >
                    <Icon className="h-7 w-7 text-[#0c1b2a]" strokeWidth={2.4} />
                  </div>
                  <span
                    className={`text-[10px] font-mono uppercase tracking-[0.3em] px-2 py-1 rounded border ${m.badgeCls}`}
                  >
                    {m.badgeText}
                  </span>
                </div>

                <h2
                  className={`mt-6 font-display text-xl font-bold ${
                    m.disabled ? "text-slate-300" : ""
                  }`}
                >
                  {m.title}
                </h2>
                <p
                  className={`mt-2 text-sm leading-relaxed ${
                    m.disabled ? "text-slate-500" : "text-slate-400"
                  }`}
                >
                  {m.desc}
                </p>

                <div
                  className={`mt-5 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest ${
                    m.disabled ? "text-slate-600" : "text-slate-500"
                  }`}
                >
                  {m.tags.map((t) => (
                    <span
                      key={t}
                      className={`px-2 py-1 rounded ${
                        m.disabled ? "bg-[#0b1521]" : "bg-[#122032]"
                      }`}
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between text-sm">
                  {m.disabled ? (
                    <>
                      <div className="flex items-center gap-2 text-slate-500">
                        <Clock className="h-4 w-4 text-[#fbbf24]" />
                        Dalam pengembangan
                      </div>
                      <div className="text-slate-600 text-xs italic">
                        Segera hadir
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-slate-400 text-xs">
                        <Wrench className="h-4 w-4 text-[#0ea5e9]" />
                        Modul aktif
                      </div>
                      <div className="flex items-center gap-2 text-[#7dd3fc] font-semibold group-hover:gap-3 transition-all">
                        Buka
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </>
                  )}
                </div>
              </>
            );

            if (m.disabled) {
              return (
                <div
                  key={m.key}
                  data-testid={m.testid}
                  aria-disabled="true"
                  className="relative rounded-2xl p-7 bg-gradient-to-br from-[#0d1a26] to-[#050f18] border border-[#1e2c40] overflow-hidden cursor-not-allowed"
                >
                  <div className="absolute inset-0 opacity-[0.08] pointer-events-none stripes-wip" />
                  {commonInner}
                </div>
              );
            }
            return (
              <button
                key={m.key}
                onClick={() => navigate(m.to)}
                data-testid={m.testid}
                className="group relative text-left rounded-2xl p-7 bg-gradient-to-br from-[#0d243a] to-[#062036] border border-[#243a52] hover:border-[#0ea5e9]/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(14,165,233,0.55)] focus:outline-none focus:ring-2 focus:ring-[#0ea5e9]/60"
              >
                {commonInner}
              </button>
            );
          })}
        </div>

        <footer className="mt-16 pt-6 border-t border-[#243a52] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-slate-500">
          <div>
            <span className="text-slate-400 font-display font-semibold">
              CRLS Console Suite
            </span>{" "}
            · Failure Guidance · v1.0
          </div>
          <PnjLogo size={32} />
        </footer>
      </main>

      <style>{`
        .fg-grid {
          background-image:
            linear-gradient(rgba(245,158,11,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(245,158,11,0.05) 1px, transparent 1px);
          background-size: 42px 42px;
        }
        .stripes-wip {
          background-image: repeating-linear-gradient(
            45deg,
            #fbbf24 0px,
            #fbbf24 12px,
            transparent 12px,
            transparent 24px
          );
        }
      `}</style>
    </div>
  );
}
